/**
 * Location scouting (rebuilt per locations.md + _GLOBAL.md G1/G3/G6/G7).
 *
 * A location is a PHYSICAL place (materials, layout, scale, geography) —
 * not a vibe. The panel:
 *
 *  1. CREATE flow with a live name↔description coherence audit (G3) and a
 *     reference-photo upload available AT GENERATE time (condition the
 *     plate before the location even exists) — not just after.
 *  2. VARIATIONS: weather / time-of-day VIEWS of the SAME location
 *     (night / day / rain / golden-hour…), each with its own plate image,
 *     persisted through the store's variation API. This generalizes the
 *     old day/night tabs into a full variation system.
 *  3. AUTO PRE-ASSIGN on script ingest: scenes whose slug place matches an
 *     existing location are wired up automatically (no manual assignment).
 *  4. GAP DETECTION as an ACTIVE prompt: scenes with NO location AND
 *     locations with NO scenes are surfaced with one-click actions.
 *  5. IMPORT FROM GLOBAL: pull a location from the user's cross-project
 *     library; publish one back with "Save to global".
 *  6. SHOT-LEVEL variation assignment: per storyboard frame, choose which
 *     variation that shot uses (store.setShotVariation) — not a scene chip.
 *  7. NO scene-assignment chips (G1). Every plate carries an Edit button
 *     that opens the shared Photoshop-style image editor (G7). The edit
 *     FORM is componentized so it renders inside the shared entity drawer
 *     when a location is opened from the script (G6). The plate slot keeps
 *     `[data-koko-pano-slot]` so a 360 / pano viewer can drop in later.
 *
 * Plate images, reference photos and variations live on the StudioLocation
 * in the shared store (auto-persisted). Only the ephemeral "active tab" per
 * card is panel-local in-memory state.
 */

import { BasePanel } from "./BasePanel";
import {
  buildEmptyState,
  buildHelperParagraph,
  buildLabeledField,
  buildPixelBox,
  buildSectionLabel,
  buildStudioButton,
} from "./dom";
import {
  openImageEditor,
  openEntityDrawer,
  openModal,
  runGeneration,
  placeholderImage,
} from "../shared";
import type {
  LocationVariation,
  StudioLocation,
  StudioScene,
  StudioStore,
} from "../StudioStore";
import "../styles/location.css";

interface LocationPanelOptions {
  store: StudioStore;
}

/** Times of day we recognise in slug lines, in screenplay order. */
const TIME_OF_DAY_ORDER = [
  "dawn",
  "morning",
  "day",
  "afternoon",
  "dusk",
  "evening",
  "night",
  "continuous",
] as const;
type TimeOfDay = (typeof TIME_OF_DAY_ORDER)[number];

/** Time-of-day choices offered when authoring a variation. */
const VARIATION_TIMES = [
  "day",
  "night",
  "golden-hour",
  "dawn",
  "dusk",
  "morning",
  "afternoon",
  "evening",
] as const;

/** Weather choices offered when authoring a variation. */
const VARIATION_WEATHER = [
  "clear",
  "rain",
  "snow",
  "fog",
  "overcast",
  "storm",
] as const;

/** What we derive about a location from the script's slug lines. */
interface SlugFacts {
  intExt: "INT" | "EXT" | "INT/EXT" | null;
  times: TimeOfDay[];
  sceneIds: string[];
}

/** Sentinel for "the base plate" tab (no variation selected). */
const BASE_TAB = "__base__";

/** Warm gradient tints per time of day → drives the plate placeholder. */
const TIME_TINTS: Record<TimeOfDay, [string, string]> = {
  dawn: ["rgba(214, 154, 133, 0.32)", "rgba(150, 140, 170, 0.14)"],
  morning: ["rgba(232, 200, 140, 0.30)", "rgba(180, 200, 180, 0.12)"],
  day: ["rgba(160, 196, 220, 0.30)", "rgba(220, 210, 180, 0.12)"],
  afternoon: ["rgba(224, 188, 130, 0.32)", "rgba(190, 160, 130, 0.14)"],
  dusk: ["rgba(208, 120, 110, 0.34)", "rgba(120, 100, 150, 0.18)"],
  evening: ["rgba(150, 110, 150, 0.32)", "rgba(80, 90, 130, 0.18)"],
  night: ["rgba(70, 80, 120, 0.38)", "rgba(30, 36, 60, 0.20)"],
  continuous: ["rgba(115, 87, 74, 0.18)", "rgba(115, 87, 74, 0.06)"],
};

export class LocationPanel extends BasePanel {
  private readonly store: StudioStore;
  private nameInputEl: HTMLInputElement | null = null;
  private descriptionInputEl: HTMLTextAreaElement | null = null;
  private coherenceEl: HTMLDivElement | null = null;
  private gapsEl: HTMLDivElement | null = null;
  private listEl: HTMLDivElement | null = null;
  private storeUnsubscribers: (() => void)[] = [];
  /** Pending reference photo (data URL) staged in the CREATE flow. */
  private createReferenceImage: string | null = null;
  /** Per-card active variation id (or BASE_TAB). Ephemeral view state. */
  private activeTabByLocation = new Map<string, string>();
  /** Re-entrancy guard so auto pre-assign can't loop. */
  private reconciling = false;
  /** See CharacterCreatorPanel — diff for `.is-entering` animation. */
  private previouslyRenderedIds = new Set<string>();
  private hasRenderedOnce = false;

  constructor(options: LocationPanelOptions) {
    super();
    this.store = options.store;
  }

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");

    // ---- Create form -------------------------------------------------
    const formBox = buildPixelBox();
    formBox.fill.appendChild(
      buildHelperParagraph(
        "Scout PHYSICAL places: materials, layout, scale, geography — not mood. " +
          '"Rooftop bar, 30th floor, concrete deck, glass railing, neon signage, wet tiles" ' +
          'beats "moody nighttime energy."',
      ),
    );

    // Import-from-global lives at the top of the create box so a user can
    // pull a previously-scouted place before authoring a new one.
    const importRow = document.createElement("div");
    importRow.className = "studio-button-row";
    const importButton = buildStudioButton("Import from global library", {
      onClick: () => this.openGlobalImport(),
    });
    importButton.dataset.kokoAction = "location-import-global";
    importRow.appendChild(importButton);
    formBox.fill.appendChild(importRow);

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "studio-input";
    nameInput.placeholder = "e.g. Rooftop Bar — 30th Floor";
    nameInput.dataset.kokoAction = "location-name";
    nameInput.addEventListener("input", () => this.renderCoherence());
    this.nameInputEl = nameInput;

    const descriptionInput = document.createElement("textarea");
    descriptionInput.className = "studio-textarea";
    descriptionInput.placeholder =
      "Concrete deck, glass railing, neon signage above the bar, wet tiles underfoot, " +
      "skyline on three sides, single steel service door.";
    descriptionInput.dataset.kokoAction = "location-description";
    descriptionInput.addEventListener("input", () => this.renderCoherence());
    this.descriptionInputEl = descriptionInput;

    formBox.fill.appendChild(buildLabeledField("Name", nameInput));
    formBox.fill.appendChild(buildLabeledField("Description", descriptionInput));

    // Live name↔description physical-coherence audit.
    const coherence = document.createElement("div");
    coherence.dataset.kokoSlot = "coherence-audit";
    formBox.fill.appendChild(coherence);
    this.coherenceEl = coherence;

    // Reference photo AT GENERATE time (condition the plate up front).
    formBox.fill.appendChild(this.buildCreateReferenceSlot());

    const submitButton = buildStudioButton("Add location", {
      variant: "primary",
      onClick: () => this.handleAddLocation(false),
    });
    submitButton.dataset.kokoAction = "location-submit";

    const generateButton = buildStudioButton("Add + generate plate", {
      onClick: () => this.handleAddLocation(true),
    });
    generateButton.dataset.kokoAction = "location-generate";

    const buttonRow = document.createElement("div");
    buttonRow.className = "studio-button-row";
    buttonRow.appendChild(submitButton);
    buttonRow.appendChild(generateButton);
    formBox.fill.appendChild(buttonRow);

    root.appendChild(formBox.outer);

    // ---- Gap-detection active prompt --------------------------------
    const gapsBox = buildPixelBox();
    const gaps = document.createElement("div");
    gaps.dataset.kokoSlot = "location-gaps";
    gapsBox.fill.appendChild(gaps);
    this.gapsEl = gaps;
    // The box hides itself when there are no gaps (renderGaps toggles it).
    gapsBox.outer.dataset.kokoSlot = "location-gaps-box";
    gapsBox.outer.style.display = "none";
    root.appendChild(gapsBox.outer);

    // ---- Existing locations -----------------------------------------
    const listBox = buildPixelBox();
    listBox.fill.appendChild(buildSectionLabel("Locations"));
    const list = document.createElement("div");
    list.className = "studio-card-grid";
    listBox.fill.appendChild(list);
    this.listEl = list;
    root.appendChild(listBox.outer);

    this.renderCoherence();
    return root;
  }

  protected onMounted(): void {
    const rerender = (): void => this.renderAll();
    this.storeUnsubscribers.push(this.store.subscribe("locations", rerender));
    this.storeUnsubscribers.push(this.store.subscribe("scenes", rerender));
    this.storeUnsubscribers.push(
      this.store.subscribe("storyboardFramesBySceneId", rerender),
    );
    this.storeUnsubscribers.push(
      this.store.subscribe("shotVariationByFrameId", rerender),
    );
    this.renderAll();
  }

  protected onShown(): void {
    this.renderAll();
    if (this.nameInputEl) this.nameInputEl.focus();
  }

  protected onBeforeDestroy(): void {
    for (const unsub of this.storeUnsubscribers) unsub();
    this.storeUnsubscribers = [];
  }

  // ------------------------------------------------------------------
  // Create flow
  // ------------------------------------------------------------------

  private handleAddLocation(generate: boolean): void {
    if (!this.nameInputEl || !this.descriptionInputEl) return;
    const name = this.nameInputEl.value.trim();
    const description = this.descriptionInputEl.value.trim();
    if (name.length === 0 || description.length === 0) return;

    const created = this.store.addLocation(name, description);
    const reference = this.createReferenceImage;
    if (reference) {
      this.store.updateLocation(created.id, { referenceImage: reference });
    }

    // Reset the create form.
    this.nameInputEl.value = "";
    this.descriptionInputEl.value = "";
    this.createReferenceImage = null;
    this.renderCoherence();
    this.refreshCreateReferenceSlot();
    this.nameInputEl.focus();

    if (generate) {
      void this.generatePlate(created.id, name, description, reference, null);
    }
  }

  /**
   * Run a (stubbed) generation for a location's plate. When `variation`
   * is provided the result is stored on that variation; otherwise it
   * becomes the base panorama plate. The reference photo (if any) is
   * folded into the prompt so the seam is ready for a real provider.
   */
  private async generatePlate(
    locationId: string,
    name: string,
    description: string,
    reference: string | null,
    variation: LocationVariation | null,
  ): Promise<void> {
    const variationBits = variation
      ? ` — ${[variation.timeOfDay, variation.weather].filter(Boolean).join(", ")}`
      : "";
    const prompt =
      `Cinematic establishing plate of ${name}${variationBits}. ${description}` +
      (reference ? " (reference photo provided)" : "");
    const result = await runGeneration({
      kind: "image",
      prompt,
      aspect: "16:9",
      seed: `${locationId}:${variation?.id ?? "base"}`,
    });
    const image = result.url;
    if (!image) return;
    if (variation) {
      this.updateVariation(locationId, variation.id, { image });
    } else {
      this.store.updateLocation(locationId, { panoramaImage: image });
    }
  }

  private buildCreateReferenceSlot(): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "studio-location-ref";
    wrapper.dataset.kokoSlot = "create-reference";
    this.fillReferenceSlot(
      wrapper,
      this.createReferenceImage,
      {
        emptyLabel: "Reference photo (optional)",
        emptyHint: "Upload a scout photo to steer the generated plate.",
        filledLabel: "Reference staged",
        filledHint: "Will condition the generated plate.",
        inputAction: "location-create-reference-input",
        uploadAction: "location-create-reference-upload",
        clearAction: "location-create-reference-clear",
      },
      (dataUrl) => {
        this.createReferenceImage = dataUrl;
        this.refreshCreateReferenceSlot();
      },
      () => {
        this.createReferenceImage = null;
        this.refreshCreateReferenceSlot();
      },
    );
    return wrapper;
  }

  private refreshCreateReferenceSlot(): void {
    if (!this.rootElement) return;
    const existing = this.rootElement.querySelector<HTMLElement>(
      '[data-koko-slot="create-reference"]',
    );
    if (!existing) return;
    existing.replaceWith(this.buildCreateReferenceSlot());
  }

  // ------------------------------------------------------------------
  // Coherence audit (name ↔ physical description) — G3
  // ------------------------------------------------------------------

  private renderCoherence(): void {
    if (!this.coherenceEl) return;
    const name = this.nameInputEl?.value ?? "";
    const description = this.descriptionInputEl?.value ?? "";
    this.coherenceEl.replaceChildren();
    if (description.trim().length === 0 && name.trim().length === 0) return;
    const audit = auditCoherence(name, description);
    this.coherenceEl.appendChild(buildCoherenceBadge(audit));
  }

  // ------------------------------------------------------------------
  // Render orchestration
  // ------------------------------------------------------------------

  private renderAll(): void {
    this.reconcileAutoAssign();
    this.renderGaps();
    this.renderList();
  }

  /**
   * AUTO PRE-ASSIGN: when a scene's slug place matches exactly one
   * existing location by name and the scene has no location yet, wire it
   * up. Idempotent + re-entrancy guarded so the resulting "scenes"
   * change doesn't loop.
   */
  private reconcileAutoAssign(): void {
    if (this.reconciling) return;
    const locations = this.store.get("locations");
    const scenes = this.store.get("scenes");
    if (locations.length === 0 || scenes.length === 0) return;

    const byNormalizedName = new Map<string, string[]>();
    for (const location of locations) {
      const key = normalizeName(location.name);
      const ids = byNormalizedName.get(key) ?? [];
      ids.push(location.id);
      byNormalizedName.set(key, ids);
    }

    const updates: Array<{ scene: StudioScene; locationId: string }> = [];
    for (const scene of scenes) {
      if (scene.locationId) continue;
      const slug = parseSlug(this.sceneSlugText(scene));
      if (!slug?.place) continue;
      const matches = byNormalizedName.get(normalizeName(slug.place));
      // Only auto-assign when the match is unambiguous.
      if (matches && matches.length === 1) {
        updates.push({ scene, locationId: matches[0] });
      }
    }
    if (updates.length === 0) return;

    this.reconciling = true;
    try {
      for (const { scene, locationId } of updates) {
        this.store.updateScene(scene.id, scene.body, { locationId });
      }
    } finally {
      this.reconciling = false;
    }
  }

  // ------------------------------------------------------------------
  // Gap detection — active prompt (locations.md #4)
  // ------------------------------------------------------------------

  private renderGaps(): void {
    if (!this.gapsEl) return;
    this.gapsEl.replaceChildren();
    const box = this.rootElement?.querySelector<HTMLElement>(
      '[data-koko-slot="location-gaps-box"]',
    );

    const scenes = this.store.get("scenes");
    const locations = this.store.get("locations");
    const facts = this.deriveSlugFacts(locations);
    const knownNames = new Set(locations.map((l) => normalizeName(l.name)));

    // (a) Scenes with NO location — split into "missing entry" (slug names
    // a place we haven't scouted) vs "unassigned" (no resolvable place).
    const missingPlaces = new Map<string, string>();
    let unassignedScenes = 0;
    for (const scene of scenes) {
      if (scene.locationId) continue;
      const slug = parseSlug(this.sceneSlugText(scene));
      if (slug?.place && !knownNames.has(normalizeName(slug.place))) {
        const key = normalizeName(slug.place);
        if (!missingPlaces.has(key)) missingPlaces.set(key, slug.place);
      } else {
        unassignedScenes += 1;
      }
    }

    // (b) Locations with NO scenes.
    const orphanLocations = locations.filter(
      (l) => (facts.get(l.id)?.sceneIds.length ?? 0) === 0,
    );

    const hasGaps =
      missingPlaces.size > 0 ||
      unassignedScenes > 0 ||
      orphanLocations.length > 0;
    if (box) box.style.display = hasGaps ? "" : "none";
    if (!hasGaps) return;

    const banner = document.createElement("div");
    banner.className = "studio-location-gaps";

    const title = document.createElement("div");
    title.className = "studio-location-gaps-title";
    title.textContent = "Let's close the gaps";
    banner.appendChild(title);

    if (missingPlaces.size > 0) {
      banner.appendChild(
        this.buildGapGroup(
          missingPlaces.size === 1
            ? "1 scene references a location you haven't scouted yet — add it:"
            : `${missingPlaces.size} scripted locations have no entry — add them:`,
          Array.from(missingPlaces.values()).map((place) => ({
            label: place,
            action: "location-gap-add",
            onClick: () => this.prefillFromSlug(place),
          })),
        ),
      );
    }

    if (unassignedScenes > 0) {
      const note = document.createElement("div");
      note.className = "studio-location-gaps-note";
      note.dataset.kokoSlot = "gap-unassigned";
      note.textContent =
        unassignedScenes === 1
          ? "1 scene has no location. Add a slug line (INT./EXT.) or assign one in the script."
          : `${unassignedScenes} scenes have no location. Add slug lines or assign them in the script.`;
      banner.appendChild(note);
    }

    if (orphanLocations.length > 0) {
      banner.appendChild(
        this.buildGapGroup(
          orphanLocations.length === 1
            ? "1 location isn't used by any scene. Reference it in the script, or remove it:"
            : `${orphanLocations.length} locations aren't used by any scene. Reference them, or remove them:`,
          orphanLocations.map((location) => ({
            label: `${location.name} ✕`,
            action: "location-gap-orphan",
            onClick: () => this.store.deleteLocation(location.id),
          })),
        ),
      );
    }

    this.gapsEl.appendChild(banner);
  }

  private buildGapGroup(
    label: string,
    items: Array<{ label: string; action: string; onClick: () => void }>,
  ): HTMLElement {
    const group = document.createElement("div");
    group.className = "studio-location-gaps-group";
    const lead = document.createElement("div");
    lead.className = "studio-location-gaps-lead";
    lead.textContent = label;
    group.appendChild(lead);
    const row = document.createElement("div");
    row.className = "studio-location-gaps-row";
    for (const item of items) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "studio-location-gaps-chip";
      chip.dataset.kokoAction = item.action;
      chip.textContent = item.label;
      chip.addEventListener("click", item.onClick);
      row.appendChild(chip);
    }
    group.appendChild(row);
    return group;
  }

  private prefillFromSlug(place: string): void {
    if (!this.nameInputEl) return;
    this.nameInputEl.value = titleCase(place);
    this.renderCoherence();
    this.nameInputEl.scrollIntoView({ behavior: "smooth", block: "center" });
    this.descriptionInputEl?.focus();
  }

  // ------------------------------------------------------------------
  // Card list
  // ------------------------------------------------------------------

  private renderList(): void {
    if (!this.listEl) return;
    this.listEl.replaceChildren();
    const locations = this.store.get("locations");
    if (locations.length === 0) {
      this.listEl.appendChild(
        buildEmptyState(
          "No locations yet. Add one above, import from global, or ask Koko.",
        ),
      );
      this.previouslyRenderedIds = new Set();
      return;
    }
    const factsByLocation = this.deriveSlugFacts(locations);
    const nextRenderedIds = new Set<string>();
    let staggerIndex = 0;
    for (const location of locations) {
      const isNew =
        this.hasRenderedOnce && !this.previouslyRenderedIds.has(location.id);
      const facts = factsByLocation.get(location.id) ?? {
        intExt: null,
        times: [],
        sceneIds: [],
      };
      const cardEl = this.buildLocationCard(location, facts, isNew);
      if (isNew) cardEl.style.setProperty("--stagger", String(staggerIndex++));
      this.listEl.appendChild(cardEl);
      nextRenderedIds.add(location.id);
    }
    this.previouslyRenderedIds = nextRenderedIds;
    this.hasRenderedOnce = true;
  }

  private buildLocationCard(
    location: StudioLocation,
    facts: SlugFacts,
    animateIn: boolean,
  ): HTMLElement {
    const card = buildPixelBox({ slim: true });
    card.fill.classList.add("studio-card");
    card.fill.dataset.cardId = location.id;
    if (animateIn) card.fill.classList.add("is-entering");

    const wrapper = document.createElement("div");
    wrapper.className = "studio-card-inner";

    const variations = location.variations ?? [];
    const activeTab = this.resolveActiveTab(location, facts);
    const activeVariation =
      activeTab === BASE_TAB
        ? null
        : variations.find((v) => v.id === activeTab) ?? null;

    // ---- Plate (active variation OR base) + Edit (G7) ----
    wrapper.appendChild(this.buildPlate(location, facts, activeVariation));

    // ---- Variation tabs (generalized day/night) ----
    wrapper.appendChild(this.buildVariationTabs(location, facts, activeTab));

    // ---- Auto-read INT/EXT + time tags ----
    const tags = this.buildSlugTags(facts);
    if (tags) wrapper.appendChild(tags);

    const meta = document.createElement("div");
    meta.className = "studio-card-meta";
    const sceneCount = facts.sceneIds.length;
    meta.textContent =
      sceneCount > 0
        ? `${location.id} · ${sceneCount} scene${sceneCount === 1 ? "" : "s"}`
        : `${location.id} · unused`;

    const title = document.createElement("div");
    title.className = "studio-card-title";
    title.textContent = location.name;

    const body = document.createElement("div");
    body.className = "studio-card-body";
    body.textContent = location.description;

    // Coherence audit badge (read-only) on the card.
    const audit = auditCoherence(location.name, location.description);
    const auditBadge = buildCoherenceBadge(audit);
    auditBadge.dataset.kokoSlot = "card-coherence";

    // ---- Reference photo on the card ----
    const refUpload = this.buildCardReferenceSlot(location);

    // ---- Shot-level variation assignment ----
    const shotControl = this.buildShotVariationControl(location, facts);

    // ---- Actions ----
    const actions = document.createElement("div");
    actions.className = "studio-card-actions";

    const editButton = buildStudioButton("Edit", {
      onClick: () => this.openLocationDrawer(location.id),
    });
    editButton.dataset.kokoAction = "location-edit";
    editButton.dataset.id = location.id;

    const globalButton = buildStudioButton(
      location.globalLocationId ? "Update global" : "Save to global",
      { onClick: () => this.handleSaveToGlobal(location.id) },
    );
    globalButton.dataset.kokoAction = "location-save-global";
    globalButton.dataset.id = location.id;

    const deleteButton = buildStudioButton("Delete", {
      variant: "danger",
      onClick: () => {
        this.activeTabByLocation.delete(location.id);
        this.store.deleteLocation(location.id);
      },
    });
    deleteButton.dataset.kokoAction = "location-delete";
    deleteButton.dataset.id = location.id;

    actions.appendChild(editButton);
    actions.appendChild(globalButton);
    actions.appendChild(deleteButton);

    wrapper.appendChild(meta);
    wrapper.appendChild(title);
    wrapper.appendChild(body);
    wrapper.appendChild(auditBadge);
    wrapper.appendChild(refUpload);
    if (shotControl) wrapper.appendChild(shotControl);
    wrapper.appendChild(actions);

    card.fill.appendChild(wrapper);
    return card.outer;
  }

  /**
   * The headline cinematic plate. Shows the active variation's image (or
   * the base panorama, or a time-tinted placeholder). The slot keeps
   * `[data-koko-pano-slot]` so a future 360 / pano viewer drops in here.
   * An overlaid Edit button opens the shared image editor (G7).
   */
  private buildPlate(
    location: StudioLocation,
    facts: SlugFacts,
    variation: LocationVariation | null,
  ): HTMLElement {
    const pano = document.createElement("div");
    pano.className = "studio-location-pano";
    pano.dataset.locationId = location.id;

    const eyebrow = document.createElement("div");
    eyebrow.className = "studio-location-pano-eyebrow";
    eyebrow.textContent = variation ? variation.label : "360° plate";
    pano.appendChild(eyebrow);

    const editButton = document.createElement("button");
    editButton.type = "button";
    editButton.className = "studio-location-pano-edit";
    editButton.dataset.kokoAction = "location-plate-edit";
    editButton.dataset.id = location.id;
    editButton.textContent = "Edit";
    editButton.addEventListener("click", () =>
      this.openPlateEditor(location, variation),
    );
    pano.appendChild(editButton);

    const slot = document.createElement("div");
    slot.className = "studio-location-pano-slot";
    slot.dataset.kokoPanoSlot = location.id;

    const image = variation ? variation.image : location.panoramaImage;
    if (image) {
      const img = document.createElement("img");
      img.className = "studio-location-pano-image";
      img.src = image;
      img.alt = `${location.name} plate`;
      slot.appendChild(img);
    } else {
      const plate = document.createElement("div");
      plate.className = "studio-location-pano-plate";
      const tintKey = this.plateTintKey(facts, variation);
      const [tintA, tintB] = TIME_TINTS[tintKey];
      plate.style.setProperty("--pano-tint-a", tintA);
      plate.style.setProperty("--pano-tint-b", tintB);
      slot.appendChild(plate);

      const caption = document.createElement("div");
      caption.className = "studio-location-pano-caption";
      caption.textContent = variation
        ? `${variation.label} — generate or upload a plate`
        : facts.times[0]
          ? `establishing plate — ${facts.times[0]}`
          : "establishing plate — generate or upload one";
      slot.appendChild(caption);
    }

    pano.appendChild(slot);
    return pano;
  }

  private plateTintKey(
    facts: SlugFacts,
    variation: LocationVariation | null,
  ): TimeOfDay {
    if (variation?.timeOfDay) {
      const t = matchTimeOfDay(variation.timeOfDay);
      if (t) return t;
      if (variation.timeOfDay === "golden-hour") return "dusk";
    }
    return facts.times[0] ?? "continuous";
  }

  private buildVariationTabs(
    location: StudioLocation,
    _facts: SlugFacts,
    activeTab: string,
  ): HTMLElement {
    const tabs = document.createElement("div");
    tabs.className = "studio-location-variations";
    tabs.dataset.kokoSlot = "variation-tabs";

    const baseTab = document.createElement("button");
    baseTab.type = "button";
    baseTab.className = "studio-location-variation-tab";
    baseTab.dataset.kokoAction = "location-variation-tab";
    baseTab.dataset.variation = BASE_TAB;
    baseTab.dataset.id = location.id;
    baseTab.textContent = "Base";
    if (activeTab === BASE_TAB) baseTab.classList.add("is-active");
    baseTab.addEventListener("click", () => {
      this.activeTabByLocation.set(location.id, BASE_TAB);
      this.renderList();
    });
    tabs.appendChild(baseTab);

    for (const variation of location.variations ?? []) {
      const tab = document.createElement("button");
      tab.type = "button";
      tab.className = "studio-location-variation-tab";
      tab.dataset.kokoAction = "location-variation-tab";
      tab.dataset.variation = variation.id;
      tab.dataset.id = location.id;
      if (variation.id === activeTab) tab.classList.add("is-active");
      tab.addEventListener("click", () => {
        this.activeTabByLocation.set(location.id, variation.id);
        this.renderList();
      });

      const label = document.createElement("span");
      label.textContent = variation.label;
      tab.appendChild(label);

      const remove = document.createElement("span");
      remove.className = "studio-location-variation-remove";
      remove.textContent = "✕";
      remove.dataset.kokoAction = "location-variation-remove";
      remove.dataset.variation = variation.id;
      remove.dataset.id = location.id;
      remove.addEventListener("click", (event) => {
        event.stopPropagation();
        this.removeVariation(location.id, variation.id);
      });
      tab.appendChild(remove);

      tabs.appendChild(tab);
    }

    const addTab = document.createElement("button");
    addTab.type = "button";
    addTab.className = "studio-location-variation-add";
    addTab.dataset.kokoAction = "location-add-variation";
    addTab.dataset.id = location.id;
    addTab.textContent = "+ Variation";
    addTab.addEventListener("click", () => this.openVariationModal(location));
    tabs.appendChild(addTab);

    return tabs;
  }

  private buildSlugTags(facts: SlugFacts): HTMLElement | null {
    if (!facts.intExt && facts.times.length === 0) return null;
    const row = document.createElement("div");
    row.className = "studio-location-tags";

    if (facts.intExt) {
      const tag = document.createElement("span");
      tag.className = "studio-location-tag";
      tag.classList.add(facts.intExt === "EXT" ? "is-ext" : "is-int");
      tag.textContent = facts.intExt;
      row.appendChild(tag);
    }
    for (const time of facts.times) {
      const tag = document.createElement("span");
      tag.className = "studio-location-tag is-time";
      tag.textContent = time;
      row.appendChild(tag);
    }
    return row;
  }

  // ------------------------------------------------------------------
  // Reference photo slot (shared between create form + card)
  // ------------------------------------------------------------------

  private buildCardReferenceSlot(location: StudioLocation): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "studio-location-ref";
    this.fillReferenceSlot(
      wrapper,
      location.referenceImage ?? null,
      {
        emptyLabel: "Reference image",
        emptyHint: "Upload a scout photo to steer the plate.",
        filledLabel: "Reference attached",
        filledHint: "Used to steer the rendered plate.",
        inputAction: "location-reference-input",
        uploadAction: "location-reference-upload",
        clearAction: "location-reference-clear",
        id: location.id,
      },
      (dataUrl) =>
        this.store.updateLocation(location.id, { referenceImage: dataUrl }),
      () => this.store.updateLocation(location.id, { referenceImage: null }),
    );
    return wrapper;
  }

  /**
   * Populate a reference-photo slot (thumb + label + upload/clear). Used
   * by both the create form (staged in-memory) and each card (store-backed)
   * so the upload ergonomics never drift between the two.
   */
  private fillReferenceSlot(
    wrapper: HTMLElement,
    image: string | null,
    opts: {
      emptyLabel: string;
      emptyHint: string;
      filledLabel: string;
      filledHint: string;
      inputAction: string;
      uploadAction: string;
      clearAction: string;
      id?: string;
    },
    onUpload: (dataUrl: string) => void,
    onClear?: () => void,
  ): void {
    wrapper.replaceChildren();

    const thumb = document.createElement("div");
    thumb.className = "studio-location-ref-thumb";
    if (image) {
      const img = document.createElement("img");
      img.src = image;
      img.alt = "reference";
      thumb.appendChild(img);
    } else {
      thumb.textContent = "▤";
    }

    const text = document.createElement("div");
    text.className = "studio-location-ref-text";
    const label = document.createElement("div");
    label.className = "studio-location-ref-label";
    label.textContent = image ? opts.filledLabel : opts.emptyLabel;
    const hint = document.createElement("div");
    hint.className = "studio-location-ref-hint";
    hint.textContent = image ? opts.filledHint : opts.emptyHint;
    text.appendChild(label);
    text.appendChild(hint);

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.className = "studio-location-hidden-input";
    fileInput.dataset.kokoAction = opts.inputAction;
    if (opts.id) fileInput.dataset.id = opts.id;
    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (file) void readImageFile(file).then(onUpload);
      fileInput.value = "";
    });

    const uploadButton = buildStudioButton(image ? "Replace" : "Upload", {
      onClick: () => fileInput.click(),
    });
    uploadButton.dataset.kokoAction = opts.uploadAction;
    if (opts.id) uploadButton.dataset.id = opts.id;

    wrapper.appendChild(thumb);
    wrapper.appendChild(text);
    wrapper.appendChild(uploadButton);
    wrapper.appendChild(fileInput);

    if (image && onClear) {
      const clearButton = buildStudioButton("Clear", { onClick: onClear });
      clearButton.dataset.kokoAction = opts.clearAction;
      if (opts.id) clearButton.dataset.id = opts.id;
      wrapper.appendChild(clearButton);
    }
  }

  // ------------------------------------------------------------------
  // Shot-level variation assignment (locations.md #6)
  // ------------------------------------------------------------------

  private buildShotVariationControl(
    location: StudioLocation,
    facts: SlugFacts,
  ): HTMLElement | null {
    const variations = location.variations ?? [];
    if (variations.length === 0) return null;

    const framesByScene = this.store.get("storyboardFramesBySceneId");
    const shotMap = this.store.get("shotVariationByFrameId");
    const scenes = this.store.get("scenes");

    const rows: HTMLElement[] = [];
    for (const sceneId of facts.sceneIds) {
      const frames = framesByScene[sceneId] ?? [];
      if (frames.length === 0) continue;
      const scene = scenes.find((s) => s.id === sceneId);
      for (const frame of frames) {
        const row = document.createElement("div");
        row.className = "studio-location-shot-row";

        const caption = document.createElement("div");
        caption.className = "studio-location-shot-caption";
        caption.textContent =
          (scene ? `${shortHeading(scene.heading)} · ` : "") +
          (frame.caption || `shot ${frame.order + 1}`);
        row.appendChild(caption);

        const select = document.createElement("select");
        select.className = "studio-select studio-location-shot-select";
        select.dataset.kokoAction = "location-shot-variation";
        select.dataset.frameId = frame.id;
        select.dataset.id = location.id;

        const base = document.createElement("option");
        base.value = "";
        base.textContent = "Default plate";
        select.appendChild(base);
        for (const variation of variations) {
          const opt = document.createElement("option");
          opt.value = variation.id;
          opt.textContent = variation.label;
          select.appendChild(opt);
        }
        select.value = shotMap[frame.id] ?? "";
        select.addEventListener("change", () => {
          this.store.setShotVariation(frame.id, select.value || null);
        });

        row.appendChild(select);
        rows.push(row);
      }
    }
    if (rows.length === 0) return null;

    const wrapper = document.createElement("div");
    wrapper.className = "studio-location-shots";
    wrapper.dataset.kokoSlot = "shot-variations";
    const label = document.createElement("div");
    label.className = "studio-location-shots-label";
    label.textContent = "Per-shot variation";
    wrapper.appendChild(label);
    for (const row of rows) wrapper.appendChild(row);
    return wrapper;
  }

  // ------------------------------------------------------------------
  // Variation authoring modal
  // ------------------------------------------------------------------

  private openVariationModal(location: StudioLocation): void {
    const modal = openModal({
      title: `New variation — ${location.name}`,
      surface: "location-variation",
    });

    let previewImage: string | null = null;

    const timeSelect = document.createElement("select");
    timeSelect.className = "studio-select";
    timeSelect.dataset.kokoAction = "variation-time";
    for (const t of VARIATION_TIMES) {
      const opt = document.createElement("option");
      opt.value = t;
      opt.textContent = t;
      timeSelect.appendChild(opt);
    }

    const weatherSelect = document.createElement("select");
    weatherSelect.className = "studio-select";
    weatherSelect.dataset.kokoAction = "variation-weather";
    for (const w of VARIATION_WEATHER) {
      const opt = document.createElement("option");
      opt.value = w;
      opt.textContent = w;
      weatherSelect.appendChild(opt);
    }

    const labelInput = document.createElement("input");
    labelInput.type = "text";
    labelInput.className = "studio-input";
    labelInput.dataset.kokoAction = "variation-label";
    const syncLabel = (): void => {
      labelInput.value = defaultVariationLabel(
        timeSelect.value,
        weatherSelect.value,
      );
    };
    syncLabel();
    timeSelect.addEventListener("change", syncLabel);
    weatherSelect.addEventListener("change", syncLabel);

    const preview = document.createElement("div");
    preview.className = "studio-location-variation-preview";
    preview.dataset.kokoSlot = "variation-preview";
    const renderPreview = (): void => {
      preview.replaceChildren();
      if (previewImage) {
        const img = document.createElement("img");
        img.src = previewImage;
        img.alt = "variation preview";
        preview.appendChild(img);
      } else {
        const hint = document.createElement("div");
        hint.className = "studio-location-pano-caption";
        hint.textContent =
          "Generate or upload a plate for this view (optional).";
        preview.appendChild(hint);
      }
    };
    renderPreview();

    const generateButton = buildStudioButton("Generate plate", {
      onClick: async () => {
        generateButton.textContent = "Generating…";
        generateButton.setAttribute("disabled", "true");
        const result = await runGeneration({
          kind: "image",
          prompt: `Cinematic plate of ${location.name} — ${timeSelect.value}, ${weatherSelect.value}. ${location.description}`,
          aspect: "16:9",
          seed: `${location.id}:${timeSelect.value}:${weatherSelect.value}`,
        });
        previewImage = result.url;
        renderPreview();
        generateButton.textContent = "Regenerate plate";
        generateButton.removeAttribute("disabled");
      },
    });
    generateButton.dataset.kokoAction = "variation-generate";

    const uploadInput = document.createElement("input");
    uploadInput.type = "file";
    uploadInput.accept = "image/*";
    uploadInput.className = "studio-location-hidden-input";
    uploadInput.dataset.kokoAction = "variation-upload-input";
    uploadInput.addEventListener("change", () => {
      const file = uploadInput.files?.[0];
      if (file)
        void readImageFile(file).then((dataUrl) => {
          previewImage = dataUrl;
          renderPreview();
        });
      uploadInput.value = "";
    });
    const uploadButton = buildStudioButton("Upload plate", {
      onClick: () => uploadInput.click(),
    });
    uploadButton.dataset.kokoAction = "variation-upload";

    const addButton = buildStudioButton("Add variation", {
      variant: "primary",
      onClick: () => {
        const variation: LocationVariation = {
          id: localId("lv"),
          label:
            labelInput.value.trim() ||
            defaultVariationLabel(timeSelect.value, weatherSelect.value),
          timeOfDay: timeSelect.value as LocationVariation["timeOfDay"],
          weather: weatherSelect.value as LocationVariation["weather"],
          image: previewImage,
        };
        const next = [...(location.variations ?? []), variation];
        // Record the active tab BEFORE the store write so the synchronous
        // re-render it triggers lands on the new variation, not Base.
        this.activeTabByLocation.set(location.id, variation.id);
        this.store.setLocationVariations(location.id, next);
        modal.close();
      },
    });
    addButton.dataset.kokoAction = "variation-add";

    const grid = document.createElement("div");
    grid.className = "studio-location-variation-form";
    grid.appendChild(buildLabeledField("Time of day", timeSelect));
    grid.appendChild(buildLabeledField("Weather", weatherSelect));
    grid.appendChild(buildLabeledField("Label", labelInput));

    const genRow = document.createElement("div");
    genRow.className = "studio-button-row";
    genRow.appendChild(generateButton);
    genRow.appendChild(uploadButton);
    genRow.appendChild(uploadInput);

    const commitRow = document.createElement("div");
    commitRow.className = "studio-button-row";
    commitRow.appendChild(addButton);

    modal.body.appendChild(grid);
    modal.body.appendChild(preview);
    modal.body.appendChild(genRow);
    modal.body.appendChild(commitRow);
  }

  private updateVariation(
    locationId: string,
    variationId: string,
    patch: Partial<LocationVariation>,
  ): void {
    const location = this.store
      .get("locations")
      .find((l) => l.id === locationId);
    if (!location) return;
    const next = (location.variations ?? []).map((v) =>
      v.id === variationId ? { ...v, ...patch } : v,
    );
    this.store.setLocationVariations(locationId, next);
  }

  private removeVariation(locationId: string, variationId: string): void {
    const location = this.store
      .get("locations")
      .find((l) => l.id === locationId);
    if (!location) return;
    const next = (location.variations ?? []).filter(
      (v) => v.id !== variationId,
    );
    this.store.setLocationVariations(locationId, next);
    if (this.activeTabByLocation.get(locationId) === variationId) {
      this.activeTabByLocation.set(locationId, BASE_TAB);
    }
  }

  // ------------------------------------------------------------------
  // Plate image editor (G7)
  // ------------------------------------------------------------------

  private openPlateEditor(
    location: StudioLocation,
    variation: LocationVariation | null,
  ): void {
    const current =
      (variation ? variation.image : location.panoramaImage) ||
      placeholderImage(
        `${location.id}:${variation?.id ?? "base"}`,
        [],
        location.name,
        "16:9",
      );
    openImageEditor({
      title: `Edit plate — ${location.name}${
        variation ? ` (${variation.label})` : ""
      }`,
      image: current,
      promptPlaceholder: "Describe how to modify this establishing plate…",
      onDone: (final) => {
        if (!final) return;
        if (variation) {
          this.updateVariation(location.id, variation.id, { image: final });
        } else {
          this.store.updateLocation(location.id, { panoramaImage: final });
        }
      },
    });
  }

  // ------------------------------------------------------------------
  // Edit form (componentized; reused inline + in the shared drawer, G6)
  // ------------------------------------------------------------------

  /**
   * Build the location edit form. Componentized so the SAME form renders
   * both standalone and inside the shared entity drawer when a location is
   * opened from the script. `onDone` fires after a successful save/cancel.
   */
  buildLocationForm(location: StudioLocation, onDone?: () => void): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "studio-card-inner studio-location-form";

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "studio-input";
    nameInput.value = location.name;
    nameInput.dataset.kokoAction = "location-edit-name";
    nameInput.dataset.id = location.id;

    const descInput = document.createElement("textarea");
    descInput.className = "studio-textarea";
    descInput.value = location.description;
    descInput.dataset.kokoAction = "location-edit-description";
    descInput.dataset.id = location.id;

    const audit = document.createElement("div");
    audit.dataset.kokoSlot = "edit-coherence";
    const refreshAudit = (): void => {
      audit.replaceChildren(
        buildCoherenceBadge(auditCoherence(nameInput.value, descInput.value)),
      );
    };
    refreshAudit();
    nameInput.addEventListener("input", refreshAudit);
    descInput.addEventListener("input", refreshAudit);

    const actions = document.createElement("div");
    actions.className = "studio-card-actions";

    const saveButton = buildStudioButton("Save", {
      variant: "primary",
      onClick: () => {
        this.store.updateLocation(location.id, {
          name: nameInput.value.trim(),
          description: descInput.value.trim(),
        });
        onDone?.();
      },
    });
    saveButton.dataset.kokoAction = "location-edit-save";
    saveButton.dataset.id = location.id;

    const cancelButton = buildStudioButton("Cancel", {
      onClick: () => onDone?.(),
    });
    cancelButton.dataset.kokoAction = "location-edit-cancel";
    cancelButton.dataset.id = location.id;

    actions.appendChild(saveButton);
    actions.appendChild(cancelButton);

    wrapper.appendChild(buildLabeledField("Name", nameInput));
    wrapper.appendChild(buildLabeledField("Description", descInput));
    wrapper.appendChild(audit);
    wrapper.appendChild(actions);
    return wrapper;
  }

  /**
   * Open this location's edit form inside the shared entity drawer (G6).
   * Public so the script editor can open the same form when a location is
   * clicked inline.
   */
  openLocationDrawer(locationId: string): void {
    const location = this.store
      .get("locations")
      .find((l) => l.id === locationId);
    if (!location) return;
    openEntityDrawer({
      title: `Edit ${location.name}`,
      subtitle: location.id,
      render: (body) => {
        body.appendChild(this.buildLocationForm(location));
      },
    });
  }

  // ------------------------------------------------------------------
  // Global library (locations.md #5)
  // ------------------------------------------------------------------

  private handleSaveToGlobal(locationId: string): void {
    const entry = this.store.saveLocationToGlobal(locationId);
    // The store writes the entry into the global library but does NOT
    // stamp the source location, so mirror the link locally — this both
    // dedupes future publishes and flips the button to "Update global".
    if (entry?.globalLocationId) {
      this.store.updateLocation(locationId, {
        globalLocationId: entry.globalLocationId,
      });
    } else {
      this.renderList();
    }
  }

  private openGlobalImport(): void {
    const modal = openModal({
      title: "Import from global library",
      surface: "location-global-import",
    });

    const library = this.store.getGlobalLocations();
    const existingGlobalIds = new Set(
      this.store
        .get("locations")
        .map((l) => l.globalLocationId)
        .filter((id): id is string => Boolean(id)),
    );

    if (library.length === 0) {
      modal.body.appendChild(
        buildEmptyState(
          'No global locations yet. Use "Save to global" on a card to build your cross-project library.',
        ),
      );
      return;
    }

    const list = document.createElement("div");
    list.className = "studio-location-global-list";
    for (const entry of library) {
      const gid = entry.globalLocationId ?? entry.id;
      const row = document.createElement("div");
      row.className = "studio-location-global-row";

      const text = document.createElement("div");
      text.className = "studio-location-global-text";
      const name = document.createElement("div");
      name.className = "studio-location-global-name";
      name.textContent = entry.name;
      const desc = document.createElement("div");
      desc.className = "studio-location-global-desc";
      desc.textContent = entry.description;
      text.appendChild(name);
      text.appendChild(desc);

      const already = existingGlobalIds.has(gid);
      const importButton = buildStudioButton(already ? "Imported" : "Import", {
        variant: already ? undefined : "primary",
        onClick: () => {
          if (already) return;
          this.store.importGlobalLocation(gid);
          modal.close();
        },
      });
      importButton.dataset.kokoAction = "location-import-pick";
      importButton.dataset.globalId = gid;
      if (already) importButton.setAttribute("disabled", "true");

      row.appendChild(text);
      row.appendChild(importButton);
      list.appendChild(row);
    }
    modal.body.appendChild(list);
  }

  // ------------------------------------------------------------------
  // Active-tab resolution
  // ------------------------------------------------------------------

  private resolveActiveTab(location: StudioLocation, facts: SlugFacts): string {
    const variations = location.variations ?? [];
    const stored = this.activeTabByLocation.get(location.id);
    if (stored === BASE_TAB) return BASE_TAB;
    if (stored && variations.some((v) => v.id === stored)) return stored;
    // Default: if the script uses this location at a single dominant time
    // and a matching variation exists, show it; else the base plate.
    if (facts.times[0]) {
      const match = variations.find(
        (v) => v.timeOfDay && matchTimeOfDay(v.timeOfDay) === facts.times[0],
      );
      if (match) return match.id;
    }
    return BASE_TAB;
  }

  // ------------------------------------------------------------------
  // Slug derivation
  // ------------------------------------------------------------------

  private deriveSlugFacts(locations: StudioLocation[]): Map<string, SlugFacts> {
    const byId = new Map<string, SlugFacts>();
    const byNormalizedName = new Map<string, string>();
    for (const location of locations) {
      byId.set(location.id, { intExt: null, times: [], sceneIds: [] });
      byNormalizedName.set(normalizeName(location.name), location.id);
    }

    for (const scene of this.store.get("scenes")) {
      const slug = parseSlug(this.sceneSlugText(scene));
      let locationId: string | null = scene.locationId;
      if (!locationId && slug?.place) {
        locationId = byNormalizedName.get(normalizeName(slug.place)) ?? null;
      }
      if (!locationId) continue;
      const facts = byId.get(locationId);
      if (!facts) continue;

      facts.sceneIds.push(scene.id);
      if (slug) {
        if (slug.intExt) {
          if (!facts.intExt) facts.intExt = slug.intExt;
          else if (facts.intExt !== slug.intExt) facts.intExt = "INT/EXT";
        }
        if (slug.time && !facts.times.includes(slug.time)) {
          facts.times.push(slug.time);
        }
      }
    }

    for (const facts of byId.values()) {
      facts.times.sort(
        (a, b) => TIME_OF_DAY_ORDER.indexOf(a) - TIME_OF_DAY_ORDER.indexOf(b),
      );
    }
    return byId;
  }

  private sceneSlugText(scene: StudioScene): string {
    if (looksLikeSlug(scene.heading)) return scene.heading;
    const firstLine = (scene.body ?? "").split(/\r?\n/, 1)[0] ?? "";
    if (looksLikeSlug(firstLine)) return firstLine;
    return scene.heading ?? "";
  }
}

// ====================================================================
// Pure helpers (module-scope; no panel state)
// ====================================================================

interface ParsedSlug {
  intExt: "INT" | "EXT" | "INT/EXT" | null;
  place: string | null;
  time: TimeOfDay | null;
}

interface CoherenceAudit {
  level: "ok" | "warn" | "empty";
  message: string;
}

const VIBE_WORDS = [
  "moody",
  "atmospheric",
  "atmosphere",
  "vibe",
  "vibes",
  "energy",
  "feeling",
  "feel",
  "emotional",
  "dreamy",
  "ominous",
  "eerie",
  "magical",
  "vibrant",
  "evocative",
  "mysterious",
];

const PHYSICAL_WORDS = [
  // materials / finish
  "concrete", "glass", "steel", "wood", "wooden", "brick", "neon", "tile",
  "tiles", "marble", "metal", "asphalt", "stone", "iron", "leather", "chrome",
  "plaster", "carpet", "rug", "velvet", "granite", "gravel", "rust",
  // layout / scale
  "floor", "deck", "room", "wall", "walls", "ceiling", "story", "storey",
  "stories", "floors", "metre", "meter", "metres", "meters", "feet", "foot",
  "wide", "narrow", "corner", "window", "windows", "door", "doors", "railing",
  "stairs", "staircase", "balcony", "counter", "bar", "table", "shelf",
  "shelves", "pillar", "column", "beam", "arch",
  // geography
  "street", "rooftop", "roof", "hill", "coast", "harbor", "harbour", "river",
  "alley", "courtyard", "field", "forest", "desert", "mountain", "shore",
];

/**
 * Audit a location's name↔description for PHYSICALITY (G3). Flags empty /
 * thin descriptions and mood-led ("moody nighttime energy") copy that
 * lacks concrete materials / layout / scale.
 */
function auditCoherence(name: string, description: string): CoherenceAudit {
  const desc = description.trim();
  if (desc.length === 0) {
    return {
      level: "empty",
      message:
        "Add a physical description: materials, layout, scale, geography.",
    };
  }
  const lower = desc.toLowerCase();
  const physicalHits = PHYSICAL_WORDS.filter((w) =>
    new RegExp(`\\b${w}\\b`).test(lower),
  ).length;
  const vibeHits = VIBE_WORDS.filter((w) =>
    new RegExp(`\\b${w}\\b`).test(lower),
  ).length;

  if (desc.length < 25 || physicalHits === 0) {
    return {
      level: "warn",
      message:
        vibeHits > 0
          ? "Reads as mood, not place — add concrete materials, layout and scale."
          : "Thin on physical detail — add materials, layout, scale or geography.",
    };
  }
  if (vibeHits > 0 && physicalHits < 2) {
    return {
      level: "warn",
      message: "Mostly mood — anchor it with more concrete materials / layout.",
    };
  }
  void name;
  return { level: "ok", message: "Physical and concrete ✓" };
}

function buildCoherenceBadge(audit: CoherenceAudit): HTMLElement {
  const badge = document.createElement("div");
  badge.className = `studio-location-coherence is-${audit.level}`;
  badge.dataset.level = audit.level;
  badge.textContent = audit.message;
  return badge;
}

/** Does this string read like a screenplay slug line? */
function looksLikeSlug(text: string | null | undefined): boolean {
  if (!text) return false;
  return /^\s*(INT\.?\/EXT\.?|INT\.?|EXT\.?|I\/E\.?)\b/i.test(text.trim());
}

function parseSlug(raw: string): ParsedSlug | null {
  const text = (raw ?? "").trim();
  if (text.length === 0) return null;

  let intExt: ParsedSlug["intExt"] = null;
  let rest = text;

  const prefixMatch = /^\s*(INT\.?\/EXT\.?|I\/E\.?|INT\.?|EXT\.?)\s*/i.exec(text);
  if (prefixMatch) {
    const token = prefixMatch[1].toUpperCase().replace(/\./g, "");
    if (token === "INT/EXT" || token === "I/E") intExt = "INT/EXT";
    else if (token === "INT") intExt = "INT";
    else if (token === "EXT") intExt = "EXT";
    rest = text.slice(prefixMatch[0].length);
  }

  let place = rest;
  let time: TimeOfDay | null = null;
  const dashSplit = rest.split(/\s+[-–—]\s+/);
  if (dashSplit.length > 1) {
    const tail = dashSplit[dashSplit.length - 1];
    time = matchTimeOfDay(tail);
    if (time) place = dashSplit.slice(0, -1).join(" - ");
  }
  if (!time) {
    const trailing = matchTimeOfDay(rest.split(/\s+/).slice(-1)[0] ?? "");
    if (trailing) {
      time = trailing;
      place = rest.replace(/\s+\S+\s*$/, "");
    }
  }

  place = place
    .trim()
    .replace(/[.\-–—\s]+$/, "")
    .trim();
  return { intExt, place: place.length > 0 ? place : null, time };
}

function matchTimeOfDay(token: string): TimeOfDay | null {
  const normalized = token.trim().toLowerCase().replace(/[.,]/g, "");
  if (normalized === "sunset") return "dusk";
  if (normalized === "sunrise") return "dawn";
  if (normalized === "golden-hour" || normalized === "golden hour") return "dusk";
  if (normalized === "later" || normalized === "moments later") return null;
  return (TIME_OF_DAY_ORDER as readonly string[]).includes(normalized)
    ? (normalized as TimeOfDay)
    : null;
}

function defaultVariationLabel(time: string, weather: string): string {
  const t = titleCase(time.replace(/-/g, " "));
  if (weather === "clear") return t;
  return `${t} — ${titleCase(weather)}`;
}

/** Normalize a name for case/space-insensitive comparison. */
function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Title-case a slug place ("WAREHOUSE FLOOR" → "Warehouse Floor"). */
function titleCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

/** A compact scene heading for shot rows. */
function shortHeading(heading: string): string {
  const trimmed = (heading ?? "").trim();
  return trimmed.length > 28 ? `${trimmed.slice(0, 27)}…` : trimmed;
}

/** Read an image File as a data URL. */
function readImageFile(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

/** Local id generator for panel-created entities (variations). */
function localId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}
